<script setup>

import {computed, inject, ref, onMounted} from "vue";
import {useI18n} from "vue-i18n";
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { useMeteringPointMembershipsStore } from "@/stores/meteringPointMembershipsStore";
import edaTools from "@/lib/es-bill-shared-js/misc/edaTools";
import timeTools, {formatDate} from "@/lib/es-bill-shared-js/misc/timeTools";
import { DEV_MODE } from "@/config/config";

const dayZero = ref(new Date(2022,1-1,1));
const today = new Date();

const datesInterested = [
];

let currentDate = new Date(2022,6-1,1);
while(currentDate < today) {
  datesInterested.push(new Date(currentDate))
  currentDate.setMonth(currentDate.getMonth()+1);
}
datesInterested.push(new Date(today))

const { t } = useI18n();
const $log = inject('log');

const communitiesStore = useCommunitiesStore();
const mpmStore = useMeteringPointMembershipsStore();

const communitiesNotDemoNorEP = ref([]);
const membershipsNotDemoNorEP = ref([]);
const communitiesReport = ref([]);
const reportsByTimeInterested = ref([]);

async function loadData() {
  // Parallelize both fetch operations
  const fetchPromises = [];

  if (!communitiesStore.allCommunities?.length)
    fetchPromises.push(communitiesStore.fetchCommunities());

  if (!mpmStore.allMeteringPointMemberships?.length)
    fetchPromises.push(mpmStore.fetchMeteringPointMemberships());

  // Wait for both fetches to complete
  await Promise.all(fetchPromises);

  function checkEcn(ecn) {
    if (!DEV_MODE) {
      return edaTools.isEcnValidExternal(ecn);
    } else {
      return edaTools.isEcnValid(ecn);
    }
  }

  // Filter and assign the data
  communitiesNotDemoNorEP.value = communitiesStore.allCommunities.filter(c =>
    checkEcn(c.ecn) && !c.ecn.startsWith("EP")
  );

  membershipsNotDemoNorEP.value = mpmStore.allMeteringPointMemberships.filter(mpm =>
    checkEcn(mpm.ecn) && !mpm.ecn.startsWith("EP"));

  // Compute communities report
  let cr = [];
  for(const c of communitiesNotDemoNorEP.value || []) {
    const mpms = mpmStore.allMeteringPointMemberships.filter(mpm => mpm.ecid === c.ecid);
    let mpmsActive = filterMpmsForActiveDuringPeriod(mpms, dayZero.value, today);

    if(mpmsActive.length === 0)
      continue;

    mpmsActive.sort((a, b) => a.dateStart - b.dateStart);
    const mpmsActiveToday = filterMpmsForActiveDuringPeriod(mpms, today, today);

    cr.push({
      ...c,
      mpmsAll: mpms,
      mpmsActive: mpmsActive,
      dateFirstActive: mpmsActive.length ? mpmsActive[0].dateStart : null,
      mpmsActiveToday: mpmsActiveToday,
      dateStart: formatDate(c.dateStart),
      dateEnd: formatDate(c.dateEnd),
    });
  }
  cr.sort((a, b) => a.dateFirstActive - b.dateFirstActive);
  communitiesReport.value = cr;

  // Compute reports by time interested
  let result = [];
  let resultLast = null;

  for(const dateSubject of datesInterested){
    const communityActiveOnDate = filterCommunitiesActiveDuringPeriod(dateSubject, dateSubject);
    const mpmsActiveOnDate = filterMpmsForActiveDuringPeriod(membershipsNotDemoNorEP.value, dateSubject, dateSubject);


    let clientCodesUnique = [];
    for(const mpm of mpmsActiveOnDate) {
      if(clientCodesUnique.indexOf(mpm.clientCode) === -1)
        clientCodesUnique.push(mpm.clientCode);
    }

    result.push({
      dateSubject,
      mpmsActiveOnDate: mpmsActiveOnDate.length,
      communityActiveOnDate: communityActiveOnDate.length,
      clientCodesUnique: clientCodesUnique.length,
      changeMpms: resultLast ? (mpmsActiveOnDate.length/resultLast.mpmsActiveOnDate-1)*100 : 0,
      changeCommunities: resultLast ? (communityActiveOnDate.length/resultLast.communityActiveOnDate-1)*100 : 0,
      changeMembers: resultLast ? (clientCodesUnique.length/resultLast.clientCodesUnique-1)*100 : 0,
      changeMpmsAbs: resultLast ? (mpmsActiveOnDate.length-resultLast.mpmsActiveOnDate) : 0,
      changeCommunitiesAbs: resultLast ? (communityActiveOnDate.length-resultLast.communityActiveOnDate) : 0,
      changeMembersAbs: resultLast ? (clientCodesUnique.length-resultLast.clientCodesUnique) : 0,
      ratioMpToEg: mpmsActiveOnDate.length/communityActiveOnDate.length,
      ratioMeToEg: clientCodesUnique.length/communityActiveOnDate.length,
      ratioMpToMe: mpmsActiveOnDate.length/clientCodesUnique.length,
    });
    resultLast = result[result.length-1];
    console.info(resultLast);
  }
  reportsByTimeInterested.value = result.sort((a,b)=>(b.dateSubject-a.dateSubject));
}

onMounted(() => {
  loadData();
});


const tableHeader = computed(() => {
  let cols = [];
  cols = cols.concat([
    { title: "Stichtag", value: 'dateSubject', key: 'dateSubject' },

    { title: "EG#", value: 'communityActiveOnDate', key: 'communityActiveOnDate' },
    { title: "MG#", value: 'clientCodesUnique', key: 'clientCodesUnique' },
    { title: "ZP#", value: 'mpmsActiveOnDate', key: 'mpmsActiveOnDate' },

    { title: "∆ EG", value: 'changeCommunitiesAbs', key: 'changeCommunitiesAbs' },
    { title: "∆ EG", value: 'changeCommunities', key: 'changeCommunities' },

    { title: "∆ MG", value: 'changeMembersAbs', key: 'changeMembersAbs' },
    { title: "∆ MG", value: 'changeMembers', key: 'changeMembers' },

    { title: "∆ ZP", value: 'changeMpmsAbs', key: 'changeMpmsAbs' },
    { title: "∆ ZP", value: 'changeMpms', key: 'changeMpms' },

    { title: "ZP/EG", value: 'ratioMpToEg', key: 'ratioMpToEg' },
    { title: "MG/EG", value: 'ratioMeToEg', key: 'ratioMeToEg' },
    { title: "ZP/MG", value: 'ratioMpToMe', key: 'ratioMpToMe' },
  ]);

  // cols = cols.concat([ // ' d-none' hides the column but keeps the search ability
  //   { text: 'clientCode',        value: 'applicant.clientCode', align: ' d-none' },
  //   { text: 'notes',             value: 'notes',                align: ' d-none' },
  //   { text: 'notesCommunity',    value: 'notesCommunity',       align: ' d-none' },
  //   { text: 'meteringPointsAll', value: 'meteringPointsAll',    align: ' d-none' },
  // ]);

  return cols;
});



function filterMpmsForActiveDuringPeriod(mpms, dateStart, dateEnd) {
  let mpmResult = [];

  for(const mpm of mpms) {

    // never reached active state
    if(mpm.membershipStatus === "CREATED")
      continue;
    if(mpm.membershipStatus === "STARTED")
      continue;
    if(mpm.membershipStatus === "DECLINED")
      continue;
    if(mpm.membershipStatus === "WAITING_FOR_USER")
      continue;
    if(mpm.membershipStatus === "ACCEPTED")
      continue;
    if(mpm.membershipStatus === "ABORTED")
      continue;

    // ended before our time period
    if(mpm.dateEnd && mpm.dateEnd < dateStart)
      continue;
    if(mpm.dateStart > dateEnd)
      continue;

    mpmResult.push(mpm)
  }

  return mpmResult;
}

function filterCommunitiesActiveDuringPeriod(dateStart, dateEnd) {
  let result = [];

  for(const community of communitiesReport.value) {

    // community currently has only start
    if(community.dateFirstActive > dateEnd)
      continue;

    result.push(community)
  }

  return result;
}


</script>


<script>
export default {
  name: 'meteringPointsAnalysis',
}
</script>


<template>
  <v-container class="containerBase pa-0">

    <v-container class="containerStandard">

      <p class="textHeadline">{{ $t('statistics') }}</p>
      Stats.<br><br>

      Anzahl Gemeinschaften (ohne Demo oder Energiedienstleister): {{ communitiesNotDemoNorEP.value?.length }}<br>

      <br><br>

      Auszug an Stichtagen:
      Aktiver EG = zumindest 1 ZP war bereits aktiv.<br>
      Aktives MG = Mitglied hat aktiven ZP.<br>
      Aktiver ZP = ZP nimmt aktiv an Energieverteilung teil.<br>
      <u>Verschleppung/Verschlechterung der Statistik durch verschieden lange Aktivierungszeiten der ZP durch VNBs.</u><br>
      Späteres On-/Offboarding (Dienstleisterwechsel) derzeit nicht berücksichtigt.<br>
      <br>

      <v-data-table :headers="tableHeader"
                    :items="reportsByTimeInterested"
                    :hide-default-footer="false"
                    :page="1"
                    :items-per-page="25"
      >

        <template v-slot:[`item.dateSubject`]="{ value }">
          {{ timeTools.formatDate(value) }}
        </template>


        <template v-slot:[`item.changeCommunities`]="{ value }">
          {{ value.toFixed(1) }}%
        </template>
        <template v-slot:[`item.changeMembers`]="{ value }">
          {{ value.toFixed(1) }}%
        </template>
        <template v-slot:[`item.changeMpms`]="{ value }">
          {{ value.toFixed(1)}}%
        </template>

        <template v-slot:[`item.ratioMpToEg`]="{ value }">
          {{ value.toFixed(1) }}
        </template>
        <template v-slot:[`item.ratioMeToEg`]="{ value }">
          {{ value.toFixed(1) }}
        </template>
        <template v-slot:[`item.ratioMpToMe`]="{ value }">
          {{ value.toFixed(1)}}
        </template>

      </v-data-table>
      <br>

      <b>Details Gemeinschaften:</b><br>
      Mit (jemals) aktiven Zählpunkten, nach Aktivierungsdatum.<br><br>
      <div v-for="community of communitiesReport" :key="community.ecn">
        <u>{{community.ecn}}: {{community.name}}<br></u>
        ECID: {{community.ecid}}<br>
        Erster ZP aktiv:  {{ timeTools.formatDate(community.dateFirstActive) }}<br>
        ZP heute aktiv: {{ community.mpmsActiveToday.length}}<br>
        Start/Onboarding: {{ community.dateStart}}<br>
        Offboarding: {{ community.dateEnd}}<br>
        <router-link :to="{
                name: 'ViewStatisticsCommunity',
                params: {
                  ecid: community.ecid,
                },
              }" class="routerLinkNoDecoration">Statistiken</router-link><br>

        <br>
      </div>




    </v-container>
  </v-container>
</template>

<style>


</style>
